import { createContext, useContext, useMemo, type ReactNode } from "react";

import type { PlanogramBay } from "@CommonTypes/merchflow/pog/bay";
import { createSplitOffsets } from "src/components/Planogram/components/KonvaPlanogram/sceneProjection";

/**
 * Where a pog x sits in the 3D scene.
 *
 * A run that breaks at a corner or a doorway is drawn with a real gap between
 * the bays either side of the break, but the pog itself stays gapless. Every
 * mesh that reads an x off the pog moves it through here first. On a run with
 * no breaks this is the identity and nothing moves.
 */
const SplitShiftContext = createContext<(pogX: number) => number>((pogX) => pogX);

export const SplitShiftProvider = ({
  bays,
  children,
}: {
  bays: PlanogramBay[];
  children: ReactNode;
}) => {
  const toSceneX = useMemo(() => createSplitOffsets(bays).toSceneX, [bays]);

  return <SplitShiftContext.Provider value={toSceneX}>{children}</SplitShiftContext.Provider>;
};

export const useSplitShift = () => useContext(SplitShiftContext);
